import {
	Card,
	Button,
	Table,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Input,
	Select,
	DatePicker,
	notification,
	Badge,
	Tooltip,
	Progress,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiClockDuotone,
	PiEyeDuotone,
	PiMagnifyingGlassDuotone,
	PiFunnelDuotone,
	PiGearDuotone,
	PiPlayCircleDuotone,
	PiCheckCircleDuotone,
	PiXCircleDuotone,
	PiWarningDuotone,
	PiHouseDuotone,
	PiCalendarDuotone,
	PiCpuDuotone,
	PiDatabaseDuotone,
} from "react-icons/pi";
import { requestGetCronLogList } from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

function CronLogListPage() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [cronLogs, setCronLogs] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		status: searchParams.get("status") || "",
		jobType: searchParams.get("jobType") || "",
		dateRange: null,
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Cron Logs";
		fetchCronLogs();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.status) params.set("status", filters.status);
		if (filters.jobType) params.set("jobType", filters.jobType);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchCronLogs = () => {
		setLoading(true);
		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			order: "_id:desc",
		};

		// Build filter string
		const filterParts = [];
		if (filters.status) {
			filterParts.push(`status:${filters.status}`);
		}
		if (filters.jobType) {
			filterParts.push(`jobType:${filters.jobType}`);
		}
		if (filterParts.length > 0) {
			params.filter = filterParts.join(",");
		}

		requestGetCronLogList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const cronLogsData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setCronLogs(cronLogsData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching cron logs:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch cron logs",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleTableChange = (pagination) => {
		setPagination(pagination);
	};

	const handleSearch = (keyword) => {
		setFilters((prev) => ({ ...prev, keyword }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "RUNNING":
				return "processing";
			case "COMPLETED":
				return "success";
			case "FAILED":
				return "error";
			case "CANCELLED":
				return "default";
			default:
				return "default";
		}
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case "RUNNING":
				return <PiPlayCircleDuotone />;
			case "COMPLETED":
				return <PiCheckCircleDuotone />;
			case "FAILED":
				return <PiXCircleDuotone />;
			case "CANCELLED":
				return <PiWarningDuotone />;
			default:
				return <PiClockDuotone />;
		}
	};

	const getJobTypeColor = (jobType) => {
		switch (jobType) {
			case "AUTO_BILLING":
				return "blue";
			case "SYSTEM_CLEANUP":
				return "green";
			case "BACKUP":
				return "orange";
			case "MONITORING":
				return "purple";
			default:
				return "default";
		}
	};

	const formatDuration = (duration) => {
		if (!duration) return "N/A";
		if (duration < 1000) return `${duration}ms`;
		if (duration < 60000) return `${(duration / 1000).toFixed(1)}s`;
		return `${(duration / 60000).toFixed(1)}m`;
	};

	const formatMemoryUsage = (bytes) => {
		if (!bytes) return "N/A";
		const mb = bytes / 1024 / 1024;
		return `${mb.toFixed(1)} MB`;
	};

	const columns = [
		{
			title: "Job Details",
			key: "jobDetails",
			width: 300,
			render: (_, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiGearDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500, marginBottom: 2 }}>{record.jobName}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							ID: {record._id.substring(0, 8)}...
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Job Type",
			dataIndex: "jobType",
			key: "jobType",
			width: 120,
			render: (jobType) => <Tag color={getJobTypeColor(jobType)}>{jobType}</Tag>,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status) => (
				<Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
					{status}
				</Tag>
			),
		},
		{
			title: "Duration",
			key: "duration",
			width: 100,
			render: (_, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{formatDuration(record.duration)}</div>
					{record.status === "RUNNING" && (
						<div style={{ marginTop: 4 }}>
							<Progress percent={50} size="small" status="active" showInfo={false} />
						</div>
					)}
				</div>
			),
		},
		{
			title: "Performance",
			key: "performance",
			width: 140,
			render: (_, record) => (
				<div style={{ fontSize: "12px" }}>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiDatabaseDuotone size={12} style={{ marginRight: 4, color: "#666" }} />
						<span>Memory: {formatMemoryUsage(record.metadata?.memoryUsage?.rss)}</span>
					</div>
					<div style={{ display: "flex", alignItems: "center" }}>
						<PiCpuDuotone size={12} style={{ marginRight: 4, color: "#666" }} />
						<span>
							CPU:{" "}
							{record.metadata?.cpuUsage
								? `${(record.metadata.cpuUsage / 1000).toFixed(1)}ms`
								: "N/A"}
						</span>
					</div>
				</div>
			),
		},
		{
			title: "Execution Times",
			key: "times",
			width: 180,
			render: (_, record) => (
				<div style={{ fontSize: "12px" }}>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#52c41a" }} />
						<span>Started: {dayjs(record.startedAt).format("MMM DD, HH:mm:ss")}</span>
					</div>
					{record.completedAt && (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#1890ff" }} />
							<span>Ended: {dayjs(record.completedAt).format("MMM DD, HH:mm:ss")}</span>
						</div>
					)}
				</div>
			),
		},
		{
			title: "Logs",
			key: "logs",
			width: 80,
			align: "center",
			render: (_, record) => <Badge count={record.logs?.length || 0} showZero />,
		},
		{
			title: "Actions",
			key: "actions",
			width: 80,
			fixed: "right",
			align: "right",
			render: (_, record) => (
				<Space>
					<Tooltip title="View Details">
						<Button
							type="text"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/settings/cron-logs/${record._id}`)}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	const statusOptions = [
		{ label: "All Status", value: "" },
		{ label: "Running", value: "RUNNING" },
		{ label: "Completed", value: "COMPLETED" },
		{ label: "Failed", value: "FAILED" },
		{ label: "Cancelled", value: "CANCELLED" },
	];

	const jobTypeOptions = [
		{ label: "All Types", value: "" },
		{ label: "Auto Billing", value: "AUTO_BILLING" },
		{ label: "System Cleanup", value: "SYSTEM_CLEANUP" },
		{ label: "Backup", value: "BACKUP" },
		{ label: "Monitoring", value: "MONITORING" },
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Cron Logs Management
					</Title>
					<Badge count={pagination.total} showZero></Badge>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card
					title="All Cron Logs"
					extra={
						<Space>
							<Search
								placeholder="Search logs..."
								allowClear
								enterButton={<PiMagnifyingGlassDuotone />}
								style={{ width: 250 }}
								onSearch={handleSearch}
							/>
						</Space>
					}>
					{/* Filters */}
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={6}>
							<Select
								placeholder="Filter by status"
								allowClear
								style={{ width: "100%" }}
								suffixIcon={<PiFunnelDuotone />}
								options={statusOptions}
								value={filters.status || undefined}
								onChange={(value) => handleFilterChange("status", value)}
							/>
						</Col>
						<Col span={6}>
							<Select
								placeholder="Filter by job type"
								allowClear
								style={{ width: "100%" }}
								suffixIcon={<PiFunnelDuotone />}
								options={jobTypeOptions}
								value={filters.jobType || undefined}
								onChange={(value) => handleFilterChange("jobType", value)}
							/>
						</Col>
						<Col span={6}>
							<RangePicker
								style={{ width: "100%" }}
								placeholder={["Start Date", "End Date"]}
								value={filters.dateRange}
								onChange={(dates) => handleFilterChange("dateRange", dates)}
							/>
						</Col>
					</Row>

					<Table
						columns={columns}
						dataSource={cronLogs}
						rowKey="_id"
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} cron logs`,
						}}
						onChange={handleTableChange}
						scroll={{ x: 1200 }}
						size="small"
					/>
				</Card>
			</div>
		</div>
	);
}

export default CronLogListPage;
